import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';

/**
 * SignalR connection wrapper for the DpasSuite hubs.
 *
 * A single connection is shared across the whole React app. Components
 * subscribe to specific events; the connection lifecycle is managed here
 * (connect on first use, reconnect on transient drops, fresh negotiate on
 * identity change).
 *
 * Auth flows entirely through the auth cookie set by /api/auth/login.
 * Browsers send cookies on both the negotiate (HTTP) and the WebSocket
 * upgrade, so no custom headers or query-string identity params are needed.
 * The server reads tenant + user from the authenticated principal's claims.
 *
 * Server side: AuditToolHub at /hubs/audit, [Authorize] enforced.
 */

let connectionPromise: Promise<HubConnection> | null = null;

async function buildConnection(): Promise<HubConnection> {
  const connection = new HubConnectionBuilder()
    .withUrl('/hubs/audit')
    .withAutomaticReconnect([0, 1_000, 5_000, 15_000, 30_000])
    .configureLogging(LogLevel.Warning)
    .build();

  await connection.start();
  return connection;
}

/** Lazily build (or reuse) the connection. */
export function getConnection(): Promise<HubConnection> {
  if (!connectionPromise) connectionPromise = buildConnection();
  return connectionPromise;
}

/** Tear down the connection — called by the login / logout mutations so the
 *  next subscription negotiates fresh with the new auth cookie (or none). */
export async function resetConnection(): Promise<void> {
  if (!connectionPromise) return;
  try {
    const c = await connectionPromise;
    if (c.state !== HubConnectionState.Disconnected) await c.stop();
  } catch { /* swallow — best-effort */ }
  connectionPromise = null;
}
